import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { legacyCableApi } from '@/api/legacy';
import type { RecordCableUsageDto } from '@/api/legacy';

/**
 * Get all cable spools
 */
export function useCableSpools() {
  return useQuery({
    queryKey: ['legacy', 'cable'],
    queryFn: () => legacyCableApi.getAll(),
    staleTime: 60_000, // 1 minute
  });
}

/**
 * Get active users for transfer dropdown
 */
export function useActiveUsers() {
  return useQuery({
    queryKey: ['legacy', 'users'],
    queryFn: () => legacyCableApi.getActiveUsers(),
    staleTime: 5 * 60_000, // 5 minutes
  });
}

/**
 * Record cable usage mutation
 */
export function useRecordCableUsage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: RecordCableUsageDto) => legacyCableApi.recordUsage(dto),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['legacy', 'cable'] });
      toast.success('Расход записан', {
        description: `Остаток: ${data.remaining} м`,
      });
    },
    onError: (error) => {
      const message =
        error instanceof Error ? error.message : 'Ошибка записи расхода';
      toast.error('Ошибка', { description: message });
    },
  });
}

/**
 * Transfer spool ownership (or claim for self)
 */
export function useTransferOwnership() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ buhtaId, username }: { buhtaId: number; username: string }) =>
      legacyCableApi.transferOwnership(buhtaId, username),
    onSuccess: (_, { username }) => {
      queryClient.invalidateQueries({ queryKey: ['legacy', 'cable'] });
      if (username) {
        toast.success('Бухта передана');
      } else {
        toast.success('Бухта забрана себе');
      }
    },
    onError: (error) => {
      const message =
        error instanceof Error ? error.message : 'Ошибка передачи бухты';
      toast.error('Ошибка', { description: message });
    },
  });
}

/**
 * Get usage history for a specific spool
 */
export function useCableHistory(buhtaId: number | null) {
  return useQuery({
    queryKey: ['legacy', 'cable', 'history', buhtaId],
    queryFn: () => legacyCableApi.getHistory(buhtaId!),
    enabled: buhtaId !== null,
    staleTime: 60_000, // 1 minute
  });
}

/**
 * Get all cable usage history (for history tab)
 */
export function useAllCableHistory(onlyMine: boolean) {
  return useQuery({
    queryKey: ['legacy', 'cable', 'allHistory', onlyMine],
    queryFn: () => legacyCableApi.getAllHistory(onlyMine),
    staleTime: 60_000, // 1 minute
  });
}
